import React from "react";
import { Box, Button, Card, Stack } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import { Input } from "reactstrap";
import { Link } from "react-router-dom";
import { useRegisterListOrigin } from "../Hooks/useBackTo";

import "./pageShell.styles.scss";

export const PageShell = ({
  title,
  description,
  actions,
  stats = [],
  children,
  className = "",
  compact = false,
}) => {
  // Task 24795 - every list page already tells us its human name here, so this
  // is the one place that can register "you were on the Jobs list" without
  // touching 122 pages. It cannot misfire on a detail page: those render
  // SingleShell, not PageShell.
  useRegisterListOrigin({
    label: title ? `Back to ${title}` : null,
    enabled: !!title,
  });

  return (
    <section
      className={`page-shell ${compact ? "page-shell--compact" : ""} ${className}`.trim()}
    >
      <div className="page-shell__hero">
        <div>
          {title && <h1>{title}</h1>}
          {description && <p>{description}</p>}
        </div>

        {actions && <div className="page-shell__hero-actions">{actions}</div>}
      </div>

      {stats.length > 0 && (
        <div className="page-shell__stats">
          {stats.map((item, index) => (
            <div
              className="page-shell__stat-card"
              key={`${item.label}-${index}`}
            >
              <span>{item.label}</span>
              <strong>{item.value}</strong>
              {item.helper && <small>{item.helper}</small>}
            </div>
          ))}
        </div>
      )}

      {children}
    </section>
  );
};

export const PageShellPanel = ({ children, className = "" }) => {
  return (
    <Card className={`page-shell__panel ${className}`.trim()}>{children}</Card>
  );
};

export const PageShellTabs = ({ tabs = [], value, onChange, actions }) => {
  if (!tabs.length && !actions) return null;

  return (
    <div className="page-shell__tabs-wrap">
      <div className="page-shell__tabs">
        {tabs.map((tab) => {
          const active = value === tab.value;

          return (
            <button
              key={tab.value}
              type="button"
              className={active ? "is-active" : ""}
              onClick={() => onChange?.(tab.value)}
            >
              {tab.icon && (
                <span className="page-shell__tab-icon">{tab.icon}</span>
              )}
              {tab.label}
              {tab.count !== undefined && <em>{tab.count}</em>}
            </button>
          );
        })}
      </div>

      {actions && <div className="page-shell__tabs-actions">{actions}</div>}
    </div>
  );
};

export const PageShellToolbar = ({
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search...",
  onFilterClick,
  filterLabel = "Filters",
  createTo,
  createLabel = "Create",
  createIcon = <AddIcon />,
  actions,
  leftContent,
}) => {
  return (
    <div className="page-shell__toolbar">
      <div className="page-shell__toolbar-left">
        {leftContent || (
          <div className="page-shell__search">
            <SearchIcon fontSize="small" />
            <Input
              type="search"
              value={searchValue || ""}
              placeholder={searchPlaceholder}
              onChange={(event) => onSearchChange?.(event.target.value)}
            />
          </div>
        )}
      </div>

      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        className="page-shell__toolbar-actions"
      >
        {onFilterClick && (
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={onFilterClick}
            className="page-shell__filter-button"
          >
            {filterLabel}
          </Button>
        )}

        {createTo && (
          <Button
            component={Link}
            to={createTo}
            variant="contained"
            startIcon={createIcon}
            className="page-shell__add-button"
          >
            {createLabel}
          </Button>
        )}

        {actions}
      </Stack>
    </div>
  );
};

export const PageShellFooter = ({ label = "Total", value, children }) => {
  return (
    <div className="page-shell__footer">
      {children || (
        <>
          <span>{label}</span>
          <strong>{value}</strong>
        </>
      )}
    </div>
  );
};

export const PageShellEmptyState = ({
  icon,
  title,
  description,
  actionTo,
  actionLabel = "Create",
  actionIcon = <AddIcon />,
}) => {
  return (
    <div className="page-shell__empty">
      {icon && <div className="page-shell__empty-icon">{icon}</div>}
      {title && <h5>{title}</h5>}
      {description && <p>{description}</p>}

      {actionTo && (
        <Button
          component={Link}
          to={actionTo}
          variant="contained"
          startIcon={actionIcon}
          className="page-shell__add-button"
        >
          {actionLabel}
        </Button>
      )}
    </div>
  );
};

export const PageShellLoading = ({ children }) => {
  return <div className="page-shell__loading">{children}</div>;
};

export const PageShellGridWrap = ({ children, className = "" }) => {
  return (
    <Box className={`page-shell__grid-wrap ${className}`.trim()}>
      {children}
    </Box>
  );
};

export default PageShell;
